import { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  RemoveUserMutation,
  RemoveUserMutationVariables,
} from "../../graphql/mutations/__generated__/auth.mutation";
import { removeUser } from "../../graphql/mutations/auth.mutation";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import Modal from "./Modal";
import { logoutUser } from "../../redux/actions/authActions";

interface DeleteAccountModalProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  onComplete: () => void;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  showModal,
  setShowModal,
  onComplete,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  const [
    removeUserCompletely,
    { loading: removeUserLoading, error: removeUserErrors },
  ] = useMutation<RemoveUserMutation, RemoveUserMutationVariables>(removeUser);

  const handleRemoveUser = () => {
    removeUserCompletely({
      variables: {
        removeUserId: user?.id,
      },
    })
      .then(() => {
        dispatch(logoutUser(true));
      })
      .catch((err) => {
        console.error("Error removing user:", err);
      });
  };

  if (!showModal) return null;

  return (
    <div>
      <Modal show={showModal} setShow={setShowModal} canClose={true}>
        <div className="bg-white p-4 rounded-lg">
          <h1 className="text-lg font-bold mb-4">Delete Tendo Account</h1>
          <p className="text-gray-600 mb-4">
            Permanently remove your personal account and all of its content from
            the Tendo Platform. This action is irreversible. Take caution.
          </p>

          {removeUserLoading && (
            <p className="text-blue-500 mb-4">Deleting your account...</p>
          )}
          {removeUserErrors && (
            <p className="text-red-500 mb-4">
              An error occurred: {removeUserErrors.message}
            </p>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleRemoveUser}
              className="bg-red-500 text-white px-4 py-2 rounded-md mr-4"
              aria-label="Delete Account"
              disabled={removeUserLoading}
            >
              Delete Account
            </button>
            <button
              onClick={() => setShowModal(false)}
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md"
              aria-label="Cancel"
              disabled={removeUserLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DeleteAccountModal;
